import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './SourceMaterialModal.module.scss'
import { Tabs } from 'components/BlazeTabs/Tabs'
import { Item } from '@react-stately/collections'
import { Importer } from './ContentSourcePicker/Importer'
import { ReactElement, useState } from 'react'
import { FromURL } from './ContentSourcePicker/FromURL'
import { FromText } from './ContentSourcePicker/FromText'
import { FromDoc } from './ContentSourcePicker/FromDoc'
import { BareArticle } from 'components/CampaignCreationModal/CampaignCreationModal'
import { FromFile } from './ContentSourcePicker/FromFile'
import { useLoading } from 'hooks/UseLoading'
import { useDeleteSourceMaterial } from 'hooks/useSourceMaterials'
import { KnowledgeBaseSourceMaterial } from 'types'
import { Trash } from 'almanac-ui/icons'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { LinkAngled, EditContained, File02, Upload5 } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { HighDetailedSource } from 'blaze-ui/icons/24'
import { SourcePreviewModal } from 'screens/BrandKits/components/SourceMaterialModal/SourcePreviewModal'
import {
  CreateKnowledgeBaseSourceMaterialPayload,
  UpdateKnowledgeBaseSourceMaterialPayload,
} from 'api/clients/KnowledgeBaseClient'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from './MobileHeader'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { AnalysingModal } from 'screens/BrandKits/components/SourceMaterialModal/AnalysingModal'
import AlertTriangle from 'blaze-ui/icons/20/AlertTriangle'

export const CONTENT_CHAR_LIMIT = 16000

const ImporterMap: { [key in KnowledgeBaseSourceMaterial['kind']]: Importer } = {
  text: Importer.AddText,
  article: Importer.SelectDoc,
  url: Importer.ScanWebsite,
  file: Importer.UploadFiles,
}

export interface Props {
  onHandleSourceMaterialSuccess: (sourceMaterial: KnowledgeBaseSourceMaterial) => void
  sourceMaterial?: KnowledgeBaseSourceMaterial
}

export const SourceMaterialModal = ({ onHandleSourceMaterialSuccess, sourceMaterial }: Props) => {
  const { closeModal, openModal } = useModals()
  const [sampleText, setSampleText] = useState(sourceMaterial?.kind === 'text' ? sourceMaterial.content : '')
  const [url, setUrl] = useState(sourceMaterial?.source_url || '')
  const [doc, setDoc] = useState<BareArticle | null>(sourceMaterial?.source_article || null)
  const deleteSourceMaterial = useDeleteSourceMaterial()
  const [selectedImporter, setSelectedImporter] = useState<Importer>(
    sourceMaterial ? ImporterMap[sourceMaterial.kind] : Importer.ScanWebsite,
  )
  const { isMobile } = useResponsiveDesign()
  const [docContent, setDocContent] = useState<string>(sourceMaterial?.kind === 'article' ? sourceMaterial.content : '')
  const [file, setFile] = useState<File | null>(null)
  const [fileContent, setFileContent] = useState<string>(sourceMaterial?.kind === 'file' ? sourceMaterial.content : '')
  const [touched, setTouched] = useState(false)
  const [showError, setShowError] = useState(false)
  const [websiteScanFailed, setWebsiteScanFailed] = useState(false)
  const { isValid: isValidUrl } = useIsUrlCrawlable(url)

  const handleSourceMaterial = () => {
    let content: string = sampleText
    let sourceProps = {}

    if (selectedImporter === Importer.ScanWebsite) {
      content = sourceMaterial?.content || content
      if (touched) {
        return openModal(AnalysingModal, {
          onError: () => {
            setWebsiteScanFailed(true)
            setSelectedImporter(Importer.AddText)
          },
          onHandleSourceMaterialSuccess,
          sourceMaterial: { ...sourceMaterial, source_url: url } as KnowledgeBaseSourceMaterial & {
            source_url: string
          },
        })
      }
      sourceProps = { source_url: url }
    } else if (selectedImporter === Importer.SelectDoc && doc) {
      content = docContent
      sourceProps = { source_article: doc.id }
    } else if (selectedImporter === Importer.UploadFiles && fileContent) {
      content = fileContent
      if (file) sourceProps = { source_file: file }
    }

    const attributes = { color: null, content: content.substring(0, CONTENT_CHAR_LIMIT), ...sourceProps }

    openModal(SourcePreviewModal, {
      sourceMaterial: { ...sourceMaterial, ...attributes } as
        | UpdateKnowledgeBaseSourceMaterialPayload
        | CreateKnowledgeBaseSourceMaterialPayload,
      onHandleSourceMaterialSuccess,
    })
  }

  const [handleDelete, loadingDelete] = useLoading(async () => {
    if (!sourceMaterial) return

    await deleteSourceMaterial.mutateAsync(sourceMaterial.id).then(closeModal)
  })

  const isValid =
    (selectedImporter === Importer.AddText && sampleText) ||
    (selectedImporter === Importer.ScanWebsite && url.length && isValidUrl) ||
    (selectedImporter === Importer.SelectDoc && doc && docContent.length) ||
    (selectedImporter === Importer.UploadFiles && fileContent.length)

  const handleSubmit = () => {
    if (!isValid) {
      setShowError(true)
      return
    }

    handleSourceMaterial()
  }

  const title = websiteScanFailed ? (
    <div className={Styles.header}>
      <AlertTriangle size={32} />
      <Heading level={3}>This site didn't allow auto-export</Heading>
    </div>
  ) : (
    <div className={Styles.header}>
      <HighDetailedSource size={32} />
      <Heading level={3}>{sourceMaterial ? 'Update' : 'Create'} source material</Heading>
    </div>
  )

  const mobileHeader = websiteScanFailed ? (
    <MobileHeader onBack={closeModal}>This site didn't allow auto-export</MobileHeader>
  ) : (
    <MobileHeader onBack={closeModal}>{sourceMaterial ? 'Update' : 'Create'} source material</MobileHeader>
  )

  return (
    <Modal.Root
      size="sm"
      blaze
      onClose={() => {
        closeModal()
      }}
      onPressOutside={() => {
        closeModal()
      }}
      bodyClassName={Styles.modal}
      aria-label={`${sourceMaterial ? 'Update Source Material' : 'New Source Material'}`}
    >
      <ResponsiveDesign.Desktop>
        <Modal.Header title={title} />
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>{mobileHeader}</ResponsiveDesign.Mobile>
      <div className={Styles.container}>
        <div className={Styles.tabs}>
          <Tabs
            withEditorTopColor={isMobile}
            aria-label="Source material options"
            animate
            classNames={{ tab: Styles.tab }}
            onSelectionChange={(key) => {
              setSelectedImporter(key as Importer)
              setShowError(false)
            }}
            selectedKey={selectedImporter}
          >
            <Item
              textValue="Link Website"
              key={Importer.ScanWebsite}
              title={
                <div className={Styles.tabContents}>
                  {!isMobile && <LinkAngled />}
                  <span>Link Website</span>
                </div>
              }
            >
              <FromURL
                url={url}
                onChange={(url) => {
                  setUrl(url)
                  setTouched(true)
                }}
                showError={showError}
              />
            </Item>
            <Item
              textValue="Paste Text"
              key={Importer.AddText}
              title={
                <div className={Styles.tabContents}>
                  {!isMobile && <EditContained />}
                  <span>Paste Text</span>
                </div>
              }
            >
              <FromText
                sampleText={sampleText}
                showError={showError}
                fromWebsite={websiteScanFailed}
                onChange={(text) => {
                  setSampleText(text)
                  setTouched(true)
                }}
              />
            </Item>
            <Item
              textValue="Select File"
              key={Importer.SelectDoc}
              title={
                <div className={Styles.tabContents}>
                  {!isMobile && <File02 />}
                  <span>Select File</span>
                </div>
              }
            >
              <FromDoc
                onChange={(text, doc) => {
                  setDocContent(text)
                  setDoc(doc || null)
                  setTouched(true)
                }}
                selectedDoc={doc || undefined}
                fullWidth
                showError={showError}
              />
            </Item>
            <Item
              textValue="Upload File"
              key={Importer.UploadFiles}
              title={
                <div className={Styles.tabContents}>
                  {!isMobile && <Upload5 />}
                  <span>Upload File</span>
                </div>
              }
            >
              <FromFile
                initialFileName={sourceMaterial?.source_file_filename}
                initialFileUrl={sourceMaterial?.source_file_url}
                onChange={(fileContent, file) => {
                  setFile(file)
                  setFileContent(fileContent || '')
                  setTouched(true)
                }}
                showError={showError}
              />
            </Item>
          </Tabs>
        </div>
      </div>
      <Modal.Footer blaze className={Styles.footer}>
        {
          [
            !isMobile && (
              <Modal.FooterContent slot="left" key="left">
                {sourceMaterial?.id ? (
                  <Modal.FooterButton
                    isDisabled={loadingDelete}
                    onPress={handleDelete}
                    size="lg"
                    variant="tertiary"
                    frontIcon={Trash}
                  >
                    Delete
                  </Modal.FooterButton>
                ) : (
                  <Modal.FooterButton size="lg" variant="tertiary" onPress={closeModal}>
                    Cancel
                  </Modal.FooterButton>
                )}
              </Modal.FooterContent>
            ),
            <Modal.FooterContent slot="right" key="right">
              <Modal.FooterButton size="lg" isDisabled={loadingDelete} onPress={handleSubmit}>
                Analyze
              </Modal.FooterButton>
            </Modal.FooterContent>,
          ].filter(Boolean) as ReactElement | [ReactElement, ReactElement]
        }
      </Modal.Footer>
    </Modal.Root>
  )
}
